import moment from 'moment-timezone';
import sortBy from 'lodash/sortBy';
// import storeConfig from '../configuration/storeConfig';

const convertSecondsTo12Hour = (timeStamp) => {
  let hours = timeStamp / 3600;
  let minutes = (timeStamp / 60) % 60;
  let ampm = '';
  if (hours === 24) {
    hours = 23;
    minutes = 59;
  }
  hours >= 12 ? (ampm = 'PM') : (ampm = 'AM');
  return moment(`${hours}:${minutes} ${ampm}`, 'HH:mm A').format('hh:mm A');
};


const OVERNIGHT_HEAD_MIN_END = 85500;
const OVERNIGHT_TAIL_MAX_END = 12 * 3600; 

const PICKUP_FIELDS = {
  start: 'openingStart',
  end: 'openingEnd',
  noFlag: 'noPickup',
};
const DELIVERY_FIELDS = {
  start: 'deliveryStart',
  end: 'deliveryEnd',
  noFlag: 'noDelivery',
};

const nextIsoDay = (day) => String(Number(day) === 7 ? 1 : Number(day) + 1);


const getOvernightTail = (allShifts, shift, fields) => {
  if (Number(shift[fields.end]) < OVERNIGHT_HEAD_MIN_END) return null;
  const nextDay = nextIsoDay(shift.day);
  return (
    (allShifts || []).find(
      (s) =>
        s.day === nextDay &&
        !s.isOrderingPaused &&
        !s[fields.noFlag] &&
        Number(s[fields.start]) === 0 &&
        Number(s[fields.end]) > 0 &&
        Number(s[fields.end]) <= OVERNIGHT_TAIL_MAX_END
    ) || null
  );
};

const getOvernightEndObj = (date, tail, fields) =>
  moment(
    `${date.clone().add(1, 'd').format('YYYY-MM-DD')} ${convertSecondsTo12Hour(
      tail[fields.end]
    )}`,
    'YYYY-MM-DD hh:mm A'
  );

const getJoinedEndSecondsFun = (allShifts, shift, service) => {
  const fields = service === 'delivery' ? DELIVERY_FIELDS : PICKUP_FIELDS;
  const tail = getOvernightTail(allShifts, shift, fields);
  return tail
    ? 24 * 3600 + Number(tail[fields.end])
    : Number(shift[fields.end]);
};

const getRunningOvernightHead = (allShifts, nowMoment, fields) => {
  const seconds =
    nowMoment.hour() * 3600 + nowMoment.minute() * 60 + nowMoment.second();
  return (
    (allShifts || []).find(
      (s) =>
        s.day === nowMoment.format('E') &&
        !s.isOrderingPaused &&
        !s[fields.noFlag] &&
        seconds >= Number(s[fields.start]) &&
        Number(s[fields.end]) >= OVERNIGHT_HEAD_MIN_END
    ) || null
  );
};

// All midnight handling for the slot arrays lives here, applied to the
// finished array so the branch logic below stays untouched. A shift past
// midnight is stored as two docs (head to ~11:59 PM, tail from 12:00 AM), so
// the branches drop the ASAP slot once `now + prep` rolls past midnight even
// though the store is still trading. When a head is running and its tail can
// accommodate `now + prep`, this:
//   1. re-adds the missing ASAP slot to today's bucket (or fixes the shiftId
//      on one the branch created), attributed to the doc owning the ready
//      time, and
//   2. drops tomorrow's slots earlier than the ready time — the kitchen
//      cannot make them, and ASAP already covers that window.
// With no running head (service closed for the night), it does nothing:
// tomorrow's midnight slots are genuine pre-orders and keep their ladder.
const applyOvernightAsap = (
  storeConfig,
  allShifts,
  orderSetup,
  days,
  service
) => {
  const fields = service === 'delivery' ? DELIVERY_FIELDS : PICKUP_FIELDS;
  const prep = Number(
    service === 'delivery' ? orderSetup.deliveryTime : orderSetup.pickUpTime
  );
  const todayBucket = days[0];
  if (!todayBucket || !todayBucket.times) return;
  const existingAsap = todayBucket.times.find(
    (x) => x.alternateDisplay === 'ASAP'
  );
  const now = moment().tz(storeConfig.timeZone);
  if (todayBucket.dateString !== now.format('YYYY-MM-DD')) return;
  const head = getRunningOvernightHead(allShifts, now, fields);
  if (!head) return;
  const tail = getOvernightTail(allShifts, head, fields);
  if (!tail) return;
  const ready = moment(
    now.format('YYYY-MM-DD hh:mm A'),
    'YYYY-MM-DD hh:mm A'
  ).add(prep, 'minutes');
  const readyIsTomorrow =
    ready.format('YYYY-MM-DD') !== now.format('YYYY-MM-DD');
  const trimCoveredTomorrowSlots = () => {
    const tomorrow = days.find(
      (d) => d.dateString === now.clone().add(1, 'd').format('YYYY-MM-DD')
    );
    if (!tomorrow?.times?.length) return;
    const kept = tomorrow.times.filter((t) => t.mTimeStamp >= ready.valueOf());
    if (kept.length === tomorrow.times.length) return;
    tomorrow.times = kept;
    tomorrow.timeGroup = { displayTime: kept.map((t) => t.display) };
    tomorrow.valueGroup = { displayTime: kept.length ? kept[0].display : '' };
  };
  if (existingAsap) {
    // The branch created the slot (isOrderAllowedTillClosing gates on `now`)
    // but stamped it with the head's id; a ready time past midnight belongs
    // to the tail doc.
    if (readyIsTomorrow && existingAsap.shiftId === head._id) {
      existingAsap.shiftId = tail._id;
    }
    trimCoveredTomorrowSlots();
    return;
  }
  const fits =
    orderSetup && orderSetup.isOrderAllowedTillClosing
      ? true
      : ready.isBefore(getOvernightEndObj(now, tail, fields));
  if (!fits) return;
  const displayTime = `${prep} mins`;
  todayBucket.times.unshift({
    display: displayTime,
    alternateDisplay: 'ASAP',
    timeStamp: head[fields.start],
    displayForUse: ready.format('hh:mm A'),
    shiftId: readyIsTomorrow ? tail._id : head._id,
    timeObjFinal: ready.format('YYYY-MM-DD'),
    mTimeStamp: ready.valueOf(),
  });
  todayBucket.timeGroup.displayTime.unshift(displayTime);
  todayBucket.valueGroup.displayTime = displayTime;
  trimCoveredTomorrowSlots();
};

// Debug: dump the generated day/time arrays exactly as the UI receives them.
const logTimeArrays = (label, storeConfig, days) => {
  /* eslint-disable no-console */
  console.log(
    `[getTimeSlots] ${label} @ ${moment()
      .tz(storeConfig.timeZone)
      .format('ddd YYYY-MM-DD HH:mm:ss')}`,
    days.map((d) => ({
      tab: d.display,
      date: d.dateString,
      count: d.times.length,
      times: d.times.map(
        (t) =>
          `${t.alternateDisplay ? `ASAP(${t.display})` : t.display} -> ${
            t.timeObjFinal
          } ${t.displayForUse} [shift ${t.shiftId}]`
      ),
    }))
  );
  /* eslint-enable no-console */
};

const getCurrentShiftFun = (storeConfig, allShifts, closeDates) => {
  let currentShift = {
    isPickupOpen: false,
    isDeliveryOpen: false,
    isRestaurantOpen: false,
    pickUpTimeSlot: '',
    deliverySlot: '',
    restaurantSlot: '',
    closes: '',
    pickup24: false,
    delivery24: false,
    restaurant24: false,
    endTimeStampP: 0,
    endTimeStampD: 0,
    endTimeStampR: 0,
    isNBShift: false,
    nbTimeSlot: '',
  };
  let today = moment().tz(storeConfig.timeZone);
  var day = today.format(`E`);

  let timeStamp =
    Number(today.format('HH')) * 60 * 60 +
    Number(today.format('mm')) * 60 +
    Number(today.format('ss'));
  allShifts.map((obj) => {
    if (
      obj.day === day &&
      obj.openingStartDisplay &&
      obj.openingEndDisplay &&
      !obj.isOrderingPaused
    ) {
      // An overnight shift's doc stops at 11:59 PM while trading continues in
      // the next day's doc, so the head has to stay open through midnight and
      // report the tail's closing time.
      const pickupTail = getOvernightTail(allShifts, obj, PICKUP_FIELDS);
      const pickupEnd = pickupTail ? 24 * 3600 : Number(obj.openingEnd);
      const pickupEndDisplay = pickupTail
        ? pickupTail.openingEndDisplay
        : obj.openingEndDisplay;
      if (timeStamp >= Number(obj.openingStart) && timeStamp <= pickupEnd) {
        currentShift.isPickupOpen = true;
        currentShift.pickUpTimeSlot = `${obj.openingStartDisplay} - ${pickupEndDisplay}`;
        currentShift.closes = pickupEndDisplay;
        currentShift.endTimeStampP = pickupEnd;
      } else if (
        timeStamp < Number(obj.openingStart) &&
        timeStamp < Number(obj.openingEnd)
      ) {
        currentShift.opens = obj.openingStartDisplay;
      }
      currentShift.pickup24 = obj.pickup24;
      currentShift.delivery24 = obj.delivery24;
      currentShift.restaurant24 = obj.restaurant24;
      currentShift.noPickup = obj.noPickup;
      currentShift.noDelivery = obj.noDelivery;
      currentShift.noDineIn = obj.noDineIn;
    }
    if (
      obj.day === day &&
      obj.deliveryStartDisplay &&
      obj.deliveryEndDisplay &&
      !obj.isOrderingPaused
    ) {
      // Same overnight handling as pickup above.
      const deliveryTail = getOvernightTail(allShifts, obj, DELIVERY_FIELDS);
      const deliveryEnd = deliveryTail ? 24 * 3600 : Number(obj.deliveryEnd);
      const deliveryEndDisplay = deliveryTail
        ? deliveryTail.deliveryEndDisplay
        : obj.deliveryEndDisplay;
      if (timeStamp >= Number(obj.deliveryStart) && timeStamp <= deliveryEnd) {
        currentShift.isDeliveryOpen = true;
        currentShift.deliverySlot = `${obj.deliveryStartDisplay} - ${deliveryEndDisplay}`;
        currentShift.closes = deliveryEndDisplay;
        currentShift.endTimeStampD = deliveryEnd;
      }
      currentShift.pickup24 = obj.pickup24;
      currentShift.delivery24 = obj.delivery24;
      currentShift.restaurant24 = obj.restaurant24;
      currentShift.noPickup = obj.noPickup;
      currentShift.noDelivery = obj.noDelivery;
      currentShift.noDineIn = obj.noDineIn;
    }
    if (
      obj.day === day &&
      obj.restaurantStartDisplay &&
      obj.restaurantEndDisplay
    ) {
      if (
        timeStamp >= Number(obj.restaurantStart) &&
        timeStamp <= Number(obj.restaurantEnd)
      ) {
        currentShift.isRestaurantOpen = true;
        currentShift.restaurantSlot = `${obj.restaurantStartDisplay} - ${obj.restaurantEndDisplay}`;
        currentShift.closes = obj.restaurantEndDisplay;
        currentShift.endTimeStampR = obj.restaurantEnd;
      }
      currentShift.pickup24 = obj.pickup24;
      currentShift.delivery24 = obj.delivery24;
      currentShift.restaurant24 = obj.restaurant24;
      currentShift.noPickup = obj.noPickup;
      currentShift.noDelivery = obj.noDelivery;
      currentShift.noDineIn = obj.noDineIn;
    }
    return obj;
  });
  if (currentShift.endTimeStampR === '86400') {
    var dayA =
      moment().add(1, 'day').tz(storeConfig.timeZone).weekday() === 0
        ? '7'
        : moment().tz(storeConfig.timeZone).weekday().toString();
    allShifts.map((obj) => {
      if (
        obj.day === dayA &&
        obj.restaurantStartDisplay &&
        obj.restaurantEndDisplay &&
        obj.restaurantStart === '0'
      ) {
        currentShift.isNBShift = true;
        currentShift.nbTimeSlot = `${obj.openingStartDisplay} - ${obj.openingEndDisplay}`;
        currentShift.closes = obj.restaurantEndDisplay;
      }
      return obj;
    });
  }
  let tcDates = [];
  if (closeDates.length > 0) {
    tcDates = closeDates.filter((m) => {
      return m.closeDate === today.format('YYYY-MM-DD');
    });
  }
  if (tcDates.length > 0) {
    let dateObjClosed = tcDates[0];
    if (dateObjClosed.isDeliveryClosed) currentShift.isDeliveryOpen = false;
    if (dateObjClosed.isAllClosed) {
      currentShift.isDeliveryOpen = false;
      currentShift.isPickupOpen = false;
      currentShift.isRestaurantOpen = false;
    }
  }
  return currentShift;
};

const getRestauranTimeArraysFun = (storeConfig, allShifts, closeDates) => {
  let reservationTimes = [];
  for (let i = 0; i < 7; i++) {
    if (i === 0) {
      reservationTimes.push({
        date: moment().tz(storeConfig.timeZone).add(i, 'd'),
        dateString: moment()
          .tz(storeConfig.timeZone)
          .add(i, 'd')
          .format('YYYY-MM-DD'),
        isToday: true,
      });
      reservationTimes.push({
        date: moment().tz(storeConfig.timeZone).add(i, 'd'),
        dateString: moment()
          .tz(storeConfig.timeZone)
          .add(i, 'd')
          .format('YYYY-MM-DD'),
        isToday: false,
      });
    } else {
      reservationTimes.push({
        date: moment().tz(storeConfig.timeZone).add(i, 'd'),
        dateString: moment()
          .tz(storeConfig.timeZone)
          .add(i, 'd')
          .format('YYYY-MM-DD'),
        isToday: false,
      });
    }
  }
  reservationTimes.map((obj, i) => {
    obj.display = obj.date.format('dddd');
    obj.day = obj.date.day().toString();
    let todaysShifts = allShifts.filter((s) => {
      return Number(s.day) === Number(obj.date.isoWeekday());
    });
    obj.id = i;
    let times = [],
      timeGroup = [];
    todaysShifts.map((t) => {
      if (!t.noDineIn) {
        if (!t.restaurant24) {
          if (t.openingEnd && t.openingStart) {
            if (obj.isToday) {
              let shiftStartObj = moment(
                `${obj.date.format('YYYY-MM-DD')} ${convertSecondsTo12Hour(
                  t.restaurantStart
                )}`,
                'YYYY-MM-DD hh:mm A'
              );
              let shiftEndObj = moment(
                `${obj.date.format('YYYY-MM-DD')} ${convertSecondsTo12Hour(
                  t.restaurantEnd
                )}`,
                'YYYY-MM-DD hh:mm A'
              );
              let today = moment(
                moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
                'YYYY-MM-DD hh:mm A'
              );
              if (shiftEndObj.isBefore(today)) {
                //No actions required as shift is finished
              } else if (
                shiftStartObj.isAfter(today) &&
                shiftEndObj.isAfter(today)
              ) {
                //Normal default time operation as shift is yet to start
                let tempTime = shiftStartObj;
                while (tempTime.isSameOrBefore(shiftEndObj)) {
                  let time = {
                    display: tempTime.format('hh:mm A'),
                    timeStamp: t.openingStart,
                    displayForUse: tempTime.format('hh:mm A'),
                    shiftId: t._id,
                    timeObjFinal: tempTime.format('YYYY-MM-DD'),
                    mTimeStamp: tempTime.valueOf(),
                  };
                  times.push(time);
                  timeGroup.push(tempTime.format('hh:mm A'));
                  tempTime.add(15, 'minutes');
                }
              } else if (
                shiftStartObj.isBefore(today) &&
                shiftEndObj.isAfter(today)
              ) {
                let tempTime = today.add(15, 'minutes');
                const roundedUp = Math.ceil(tempTime.minute() / 15) * 15;
                tempTime = tempTime.minute(roundedUp).second(0);
                while (tempTime.isSameOrBefore(shiftEndObj)) {
                  let time = {
                    display: tempTime.format('hh:mm A'),
                    timeStamp: t.openingStart,
                    displayForUse: tempTime.format('hh:mm A'),
                    shiftId: t._id,
                    timeObjFinal: tempTime.format('YYYY-MM-DD'),
                    mTimeStamp: tempTime.valueOf(),
                  };
                  times.push(time);
                  timeGroup.push(tempTime.format('hh:mm A'));
                  tempTime.add(15, 'minutes');
                }
              }
            } else {
              let shiftStartObj = moment(
                `${obj.date.format('YYYY-MM-DD')} ${convertSecondsTo12Hour(
                  t.restaurantStart
                )}`,
                'YYYY-MM-DD hh:mm A'
              );
              let shiftEndObj = moment(
                `${obj.date.format('YYYY-MM-DD')} ${convertSecondsTo12Hour(
                  t.restaurantEnd
                )}`,
                'YYYY-MM-DD hh:mm A'
              );
              //Normal default time operation as shift is yet to start
              let tempTime = shiftStartObj;
              while (tempTime.isSameOrBefore(shiftEndObj)) {
                let time = {
                  display: tempTime.format('hh:mm A'),
                  timeStamp: t.openingStart,
                  displayForUse: tempTime.format('hh:mm A'),
                  shiftId: t._id,
                  timeObjFinal: tempTime.format('YYYY-MM-DD'),
                  mTimeStamp: tempTime.valueOf(),
                };
                times.push(time);
                timeGroup.push(tempTime.format('hh:mm A'));
                tempTime.add(15, 'minutes');
              }
            }
            return t;
          }
        } else {
          if (obj.isToday) {
            let shiftStartObj = moment(
              `${obj.date.format('YYYY-MM-DD')} 00:00 AM}`,
              'YYYY-MM-DD hh:mm A'
            );
            let shiftEndObj = moment(
              `${obj.date.format('YYYY-MM-DD')} 11:59 PM`,
              'YYYY-MM-DD hh:mm A'
            );
            let today = moment(
              moment().tz(storeConfig.timeZone).format('YYYY-MM-DD HH:mm A'),
              'YYYY-MM-DD hh:mm A'
            );
            if (shiftEndObj.isBefore(today)) {
              //No actions required as shift is finished
            } else if (
              shiftStartObj.isAfter(today) &&
              shiftEndObj.isAfter(today)
            ) {
              //Normal default time operation as shift is yet to start
              let tempTime = shiftStartObj;
              while (tempTime.isSameOrBefore(shiftEndObj)) {
                let time = {
                  display: tempTime.format('hh:mm A'),
                  timeStamp: t.openingStart,
                  displayForUse: tempTime.format('hh:mm A'),
                  shiftId: t._id,
                  timeObjFinal: tempTime.format('YYYY-MM-DD'),
                  mTimeStamp: tempTime.valueOf(),
                };
                times.push(time);
                timeGroup.push(tempTime.format('hh:mm A'));
                tempTime.add(15, 'minutes');
              }
            } else if (
              shiftStartObj.isBefore(today) &&
              shiftEndObj.isAfter(today)
            ) {
              let tempTime = today.add(15, 'minutes');
              const roundedUp = Math.ceil(tempTime.minute() / 15) * 15;
              tempTime = tempTime.minute(roundedUp).second(0);
              while (tempTime.isSameOrBefore(shiftEndObj)) {
                let time = {
                  display: tempTime.format('hh:mm A'),
                  timeStamp: t.openingStart,
                  displayForUse: tempTime.format('hh:mm A'),
                  shiftId: t._id,
                  timeObjFinal: tempTime.format('YYYY-MM-DD'),
                  mTimeStamp: tempTime.valueOf(),
                };
                times.push(time);
                timeGroup.push(tempTime.format('hh:mm A'));
                tempTime.add(15, 'minutes');
              }
            }
          } else {
            let shiftStartObj = moment(
              `${obj.date.format('YYYY-MM-DD')} 00:00 AM}`,
              'YYYY-MM-DD hh:mm A'
            );
            let shiftEndObj = moment(
              `${obj.date.format('YYYY-MM-DD')} 11:59 PM`,
              'YYYY-MM-DD hh:mm A'
            );
            let tempTime = shiftStartObj;
            while (tempTime.isSameOrBefore(shiftEndObj)) {
              let time = {
                display: tempTime.format('hh:mm A'),
                timeStamp: t.openingStart,
                displayForUse: tempTime.format('hh:mm A'),
                shiftId: t._id,
                timeObjFinal: tempTime.format('YYYY-MM-DD'),
                mTimeStamp: tempTime.valueOf(),
              };
              times.push(time);
              timeGroup.push(tempTime.format('hh:mm A'));
              tempTime.add(15, 'minutes');
            }
          }
        }
      }
      return t;
    });
    obj.times = times;
    obj.timeGroup = { displayTime: timeGroup };
    obj.valueGroup = { displayTime: times.length > 0 ? times[0].display : '' };
    return obj;
  });
  // if (closeDates.length > 0) {
  //   reservationTimes = reservationTimes.filter(obj => {
  //     let tcDate = closeDates.filter(m => {
  //       return m.closeDate === obj.dateString && m.isAllClosed;
  //     });
  //     if (tcDate.length > 0) return false;
  //     else return true;
  //   });
  // }
  reservationTimes = reservationTimes.filter((p) => {
    return p.times.length !== 0;
  });
  return reservationTimes;
};

const getPickupTimeArraysFun = (
  storeConfig,
  allShifts,
  orderSetup,
  closeDates
) => {
  let pickUpTimes = [];
  pickUpTimes.push({
    date: moment().tz(storeConfig.timeZone),
    dateString: moment().tz(storeConfig.timeZone).format('YYYY-MM-DD'),
  });
  let preOrderingDays = orderSetup.preOrderingDays
    ? orderSetup.preOrderingDays
    : 5;
  if (orderSetup.isPreOrderingEnabled) {
    for (let i = 1; i < preOrderingDays; i++) {
      pickUpTimes.push({
        date: moment().tz(storeConfig.timeZone).add(i, 'd'),
        dateString: moment()
          .tz(storeConfig.timeZone)
          .add(i, 'd')
          .format('YYYY-MM-DD'),
      });
    }
  }

  pickUpTimes.map((obj, i) => {
    if (i === 0) obj.display = 'Today';
    else if (i === 1) obj.display = 'Tomorrow';
    else if (i < 7 && i > 1) obj.display = obj.date.format('dddd');
    else obj.display = obj.date.format('MMM DD');

    obj.day = obj.date.day().toString();
    let todaysShifts = allShifts.filter((s) => {
      return Number(s.day) === Number(obj.date.isoWeekday());
    });
    obj.id = i;
    let times = [],
      timeGroup = [];
    todaysShifts.map((t) => {
      if (!t.isOrderingPaused && !t.noPickup) {
        if (!t.pickup24) {
          if (t.openingEnd && typeof t.openingStart === `number`) {
            let shiftStartObj = moment(
              `${obj.date.format('YYYY-MM-DD')} ${convertSecondsTo12Hour(
                t.openingStart
              )}`,
              'YYYY-MM-DD hh:mm A'
            );
            let shiftEndObj = moment(
              `${obj.date.format('YYYY-MM-DD')} ${convertSecondsTo12Hour(
                t.openingEnd
              )}`,
              'YYYY-MM-DD hh:mm A'
            );
            let today = moment(
              moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
              'YYYY-MM-DD hh:mm A'
            );
            if (shiftEndObj.isBefore(today)) {
              //No actions required as shift is finished
            } else if (
              shiftStartObj.isAfter(today) &&
              shiftEndObj.isAfter(today) &&
              orderSetup.isPreOrderingEnabled
            ) {
              //Normal default time operation as shift is yet to start
              let tempTime = shiftStartObj;
              if (shiftStartObj.format('hh:mm A').toString() === '12:00 AM') {
                tempTime = shiftStartObj;
              } else {
                tempTime = shiftStartObj.add(
                  Number(orderSetup.defaultPickUpTime),
                  'minutes'
                );
                const roundedUp = Math.ceil(tempTime.minute() / 15) * 15;
                tempTime = tempTime.minute(roundedUp).second(0);
              }
              while (
                orderSetup && orderSetup.isOrderAllowedTillClosing
                  ? tempTime.isSameOrBefore(shiftEndObj)
                  : tempTime.isBefore(shiftEndObj)
              ) {
                let time = {
                  display: tempTime.format('hh:mm A'),
                  timeStamp: t.openingStart,
                  displayForUse: tempTime.format('hh:mm A'),
                  shiftId: t._id,
                  timeObjFinal: tempTime.format('YYYY-MM-DD'),
                  mTimeStamp: tempTime.valueOf(),
                };
                times.push(time);
                timeGroup.push(tempTime.format('hh:mm A'));
                tempTime.add(15, 'minutes');
              }
            } else if (
              shiftStartObj.isSameOrBefore(today) &&
              shiftEndObj.isSameOrAfter(today)
            ) {
              let todayTT = moment(
                moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
                'YYYY-MM-DD hh:mm A'
              ).add(Number(orderSetup.pickUpTime), 'minutes');
              let todayTTWithoutPickup = moment(
                moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
                'YYYY-MM-DD hh:mm A'
              );
              // Add current time, rounding off time and then go with default time
              let tempTime = today.add(
                Number(orderSetup.pickUpTime),
                'minutes'
              );
              const displayTime = `${orderSetup.pickUpTime} mins`;
              let timeD = {
                display: displayTime,
                alternateDisplay: 'ASAP',
                timeStamp: t.openingStart,
                displayForUse: todayTT.format('hh:mm A'),
                shiftId: t._id,
                timeObjFinal: todayTT.format('YYYY-MM-DD'),
                mTimeStamp: todayTT.valueOf(),
              };
              if (
                orderSetup && orderSetup.isOrderAllowedTillClosing
                  ? todayTTWithoutPickup.isSameOrBefore(shiftEndObj)
                  : todayTT.isBefore(shiftEndObj)
              ) {
                times.push(timeD);
                timeGroup.push(displayTime);
              }
              if (orderSetup.isPreOrderingEnabled) {
                const roundedUp = Math.ceil(tempTime.minute() / 15) * 15;
                tempTime = tempTime.minute(roundedUp).second(0);
                if (
                  orderSetup &&
                  orderSetup.isOrderAllowedTillClosing &&
                  tempTime.isAfter(shiftEndObj)
                ) {
                  let time = {
                    display: shiftEndObj.format('hh:mm A'),
                    timeStamp: t.openingStart,
                    displayForUse: shiftEndObj.format('hh:mm A'),
                    shiftId: t._id,
                    timeObjFinal: shiftEndObj.format('YYYY-MM-DD'),
                    mTimeStamp: shiftEndObj.valueOf(),
                  };
                  times.push(time);
                  timeGroup.push(tempTime.format('hh:mm A'));
                }
                while (
                  orderSetup && orderSetup.isOrderAllowedTillClosing
                    ? tempTime.isSameOrBefore(shiftEndObj)
                    : tempTime.isBefore(shiftEndObj)
                ) {
                  let time = {
                    display: tempTime.format('hh:mm A'),
                    timeStamp: t.openingStart,
                    displayForUse: tempTime.format('hh:mm A'),
                    shiftId: t._id,
                    timeObjFinal: tempTime.format('YYYY-MM-DD'),
                    mTimeStamp: tempTime.valueOf(),
                  };
                  times.push(time);
                  timeGroup.push(tempTime.format('hh:mm A'));
                  tempTime.add(15, 'minutes');
                }
              }
            }
            return t;
          }
        } else {
          let shiftStartObj = moment(
            `${obj.date.format('YYYY-MM-DD')} 00:00 AM}`,
            'YYYY-MM-DD hh:mm A'
          );
          let shiftEndObj = moment(
            `${obj.date.format('YYYY-MM-DD')} 11:59 PM`,
            'YYYY-MM-DD hh:mm A'
          );
          let today = moment(
            moment().tz(storeConfig.timeZone).format('YYYY-MM-DD HH:mm A'),
            'YYYY-MM-DD hh:mm A'
          );
          if (shiftEndObj.isBefore(today)) {
          } else if (
            shiftStartObj.isAfter(today) &&
            shiftEndObj.isAfter(today)
          ) {
            //Normal default time operation as shift is yet to start
            let tempTime = shiftStartObj;
            const roundedUp = Math.ceil(tempTime.minute() / 15) * 15;
            tempTime = tempTime.minute(roundedUp).second(0);
            while (tempTime.isBefore(shiftEndObj)) {
              let time = {
                display: tempTime.format('hh:mm A'),
                timeStamp: t.openingStart,
                displayForUse: tempTime.format('hh:mm A'),
                shiftId: t._id,
                timeObjFinal: tempTime.format('YYYY-MM-DD'),
                mTimeStamp: tempTime.valueOf(),
              };
              times.push(time);
              timeGroup.push(tempTime.format('hh:mm A'));
              tempTime.add(15, 'minutes');
            }
          } else if (
            shiftStartObj.isSameOrBefore(today) &&
            shiftEndObj.isSameOrAfter(today)
          ) {
            let todayTT = moment(
              moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
              'YYYY-MM-DD hh:mm A'
            ).add(Number(orderSetup.pickUpTime), 'minutes');
            let tempTime = today.add(Number(orderSetup.pickUpTime), 'minutes');

            // Add current time, rounding off time and then go with default time
            const displayTime = `${orderSetup.pickUpTime} mins`;
            let timeD = {
              display: displayTime,
              alternateDisplay: 'ASAP',
              timeStamp: t.openingStart,
              displayForUse: todayTT.format('hh:mm A'),
              shiftId: t._id,
              timeObjFinal: todayTT.format('YYYY-MM-DD'),
              mTimeStamp: todayTT.valueOf(),
            };
            times.push(timeD);
            timeGroup.push(displayTime);

            const roundedUp = Math.ceil(tempTime.minute() / 15) * 15;
            tempTime = tempTime.minute(roundedUp).second(0);
            while (tempTime.isBefore(shiftEndObj)) {
              let time = {
                display: tempTime.format('hh:mm A'),
                timeStamp: t.openingStart,
                displayForUse: tempTime.format('hh:mm A'),
                shiftId: t._id,
                timeObjFinal: tempTime.format('YYYY-MM-DD'),
                mTimeStamp: tempTime.valueOf(),
              };
              times.push(time);
              timeGroup.push(tempTime.format('hh:mm A'));
              tempTime.add(15, 'minutes');
            }
          }
        }
      }
      return t;
    });
    obj.times = times;
    obj.timeGroup = { displayTime: timeGroup };
    obj.valueGroup = { displayTime: times.length > 0 ? times[0].display : '' };
    return obj;
  });

  applyOvernightAsap(storeConfig, allShifts, orderSetup, pickUpTimes, 'pickup');

  if (closeDates.length > 0) {
    pickUpTimes = pickUpTimes.filter((obj) => {
      let tcDate = closeDates.filter((m) => {
        return m.closeDate === obj.dateString && m.isAllClosed;
      });
      if (tcDate.length > 0) return false;
      else return true;
    });
  }

  pickUpTimes = pickUpTimes.filter((p) => {
    return p.times.length !== 0;
  });
  pickUpTimes =
    pickUpTimes.length > preOrderingDays
      ? pickUpTimes.slice(0, preOrderingDays)
      : pickUpTimes;
  pickUpTimes = pickUpTimes.map((p) => {
    // ASAP stays first within its day: near midnight the closing slot
    // (e.g. 11:59 PM) has a smaller timestamp than an ASAP time that has
    // already rolled past midnight, and every consumer reads times[0] as the
    // ASAP option.
    p.times = sortBy(p.times, [
      (t) => (t.alternateDisplay === 'ASAP' ? 0 : 1),
      'mTimeStamp',
    ]);
    return p;
  });
  logTimeArrays('pickup', storeConfig, pickUpTimes);
  return pickUpTimes;
};

const getDeliveryTimeArraysFun = (
  storeConfig,
  allShifts,
  orderSetup,
  closeDates
) => {
  let deliveryTimes = [];
  deliveryTimes.push({
    date: moment().tz(storeConfig.timeZone),
    dateString: moment().tz(storeConfig.timeZone).format('YYYY-MM-DD'),
  });
  let preOrderingDays = orderSetup.preOrderingDays
    ? orderSetup.preOrderingDays
    : 5;
  if (orderSetup.isPreOrderingEnabled) {
    for (let i = 1; i < preOrderingDays; i++) {
      deliveryTimes.push({
        date: moment().tz(storeConfig.timeZone).add(i, 'd'),
        dateString: moment()
          .tz(storeConfig.timeZone)
          .add(i, 'd')
          .format('YYYY-MM-DD'),
      });
    }
  }
  deliveryTimes.map((obj, i) => {
    if (i === 0) obj.display = 'Today';
    else if (i === 1) obj.display = 'Tomorrow';
    else if (i < 7 && i > 1) obj.display = obj.date.format('dddd');
    else obj.display = obj.date.format('MMM DD');

    obj.day = obj.date.day().toString();
    let todaysShifts = allShifts.filter((s) => {
      return Number(s.day) === Number(obj.date.isoWeekday());
    });
    obj.id = i;
    let times = [],
      timeGroup = [];
    todaysShifts.map((t) => {
      if (!t.isOrderingPaused && !t.noDelivery) {
        if (!t.delivery24) {
          if (t.deliveryEnd && typeof t.deliveryEnd === `number`) {
            let shiftStartObj = moment(
              `${obj.date.format('YYYY-MM-DD')} ${convertSecondsTo12Hour(
                t.deliveryStart
              )}`,
              'YYYY-MM-DD hh:mm A'
            );
            let shiftEndObj = moment(
              `${obj.date.format('YYYY-MM-DD')} ${convertSecondsTo12Hour(
                t.deliveryEnd
              )}`,
              'YYYY-MM-DD hh:mm A'
            );
            let today = moment(
              moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
              'YYYY-MM-DD hh:mm A'
            );
            if (shiftEndObj.isBefore(today)) {
              //No actions required as shift is finished
            } else if (
              shiftStartObj.isAfter(today) &&
              shiftEndObj.isAfter(today) &&
              orderSetup.isPreOrderingEnabled
            ) {
              //Normal default time operation as shift is yet to start
              let tempTime = shiftStartObj;
              if (shiftStartObj.format('hh:mm A').toString() === '12:00 AM') {
                tempTime = shiftStartObj;
              } else {
                tempTime = shiftStartObj.add(
                  Number(orderSetup.defaultDeliveryTime),
                  'minutes'
                );
                const roundedUp = Math.ceil(tempTime.minute() / 15) * 15;
                tempTime = tempTime.minute(roundedUp).second(0);
              }
              while (
                orderSetup && orderSetup.isOrderAllowedTillClosing
                  ? tempTime.isSameOrBefore(shiftEndObj)
                  : tempTime.isBefore(shiftEndObj)
              ) {
                let time = {
                  display: tempTime.format('hh:mm A'),
                  timeStamp: t.openingStart,
                  displayForUse: tempTime.format('hh:mm A'),
                  shiftId: t._id,
                  timeObjFinal: tempTime.format('YYYY-MM-DD'),
                  mTimeStamp: tempTime.valueOf(),
                };
                times.push(time);
                timeGroup.push(tempTime.format('hh:mm A'));
                tempTime.add(15, 'minutes');
              }
            } else if (
              shiftStartObj.isSameOrBefore(today) &&
              shiftEndObj.isSameOrAfter(today)
            ) {
              // Add current time, rounding off time and then go with default time
              let todayTT = moment(
                moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
                'YYYY-MM-DD hh:mm A'
              ).add(Number(orderSetup.deliveryTime), 'minutes');
              let todayTTWithoutDeliveryTime = moment(
                moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
                'YYYY-MM-DD hh:mm A'
              );
              let tempTime = today.add(
                Number(orderSetup.deliveryTime),
                'minutes'
              );
              const displayTime = `${orderSetup.deliveryTime} mins`;
              let timeD = {
                display: displayTime,
                alternateDisplay: 'ASAP',
                timeStamp: t.openingStart,
                displayForUse: todayTT.format('hh:mm A'),
                shiftId: t._id,
                timeObjFinal: todayTT.format('YYYY-MM-DD'),
                mTimeStamp: todayTT.valueOf(),
              };
              if (
                orderSetup && orderSetup.isOrderAllowedTillClosing
                  ? todayTTWithoutDeliveryTime.isSameOrBefore(shiftEndObj)
                  : todayTT.isBefore(shiftEndObj)
              ) {
                times.push(timeD);
                timeGroup.push(displayTime);
              }
              if (orderSetup.isPreOrderingEnabled) {
                const roundedUp = Math.ceil(tempTime.minute() / 15) * 15;
                tempTime = tempTime.minute(roundedUp).second(0);
                if (
                  orderSetup &&
                  orderSetup.isOrderAllowedTillClosing &&
                  tempTime.isAfter(shiftEndObj)
                ) {
                  let time = {
                    display: shiftEndObj.format('hh:mm A'),
                    timeStamp: t.openingStart,
                    displayForUse: shiftEndObj.format('hh:mm A'),
                    shiftId: t._id,
                    timeObjFinal: shiftEndObj.format('YYYY-MM-DD'),
                    mTimeStamp: shiftEndObj.valueOf(),
                  };
                  times.push(time);
                  timeGroup.push(tempTime.format('hh:mm A'));
                }
                while (
                  orderSetup && orderSetup.isOrderAllowedTillClosing
                    ? tempTime.isSameOrBefore(shiftEndObj)
                    : tempTime.isBefore(shiftEndObj)
                ) {
                  let time = {
                    display: tempTime.format('hh:mm A'),
                    timeStamp: t.openingStart,
                    displayForUse: tempTime.format('hh:mm A'),
                    shiftId: t._id,
                    timeObjFinal: tempTime.format('YYYY-MM-DD'),
                    mTimeStamp: tempTime.valueOf(),
                  };
                  times.push(time);
                  timeGroup.push(tempTime.format('hh:mm A'));
                  tempTime.add(15, 'minutes');
                }
              }
            }
            return t;
          }
        } else {
          let shiftStartObj = moment(
            `${obj.date.format('YYYY-MM-DD')} 00:00 AM}`,
            'YYYY-MM-DD hh:mm A'
          );
          let shiftEndObj = moment(
            `${obj.date.format('YYYY-MM-DD')} 24:00 PM`,
            'YYYY-MM-DD hh:mm A'
          );
          let today = moment(
            moment().tz(storeConfig.timeZone).format('YYYY-MM-DD HH:mm A'),
            'YYYY-MM-DD hh:mm A'
          );
          if (shiftEndObj.isBefore(today)) {
          } else if (
            shiftStartObj.isAfter(today) &&
            shiftEndObj.isAfter(today)
          ) {
            //Normal default time operation as shift is yet to start
            let tempTime = shiftStartObj;
            const roundedUp = Math.ceil(tempTime.minute() / 15) * 15;
            tempTime = tempTime.minute(roundedUp).second(0);
            while (tempTime.isBefore(shiftEndObj)) {
              let time = {
                display: tempTime.format('hh:mm A'),
                timeStamp: t.openingStart,
                displayForUse: tempTime.format('hh:mm A'),
                shiftId: t._id,
                timeObjFinal: tempTime.format('YYYY-MM-DD'),
                mTimeStamp: tempTime.valueOf(),
              };
              times.push(time);
              timeGroup.push(tempTime.format('hh:mm A'));
              tempTime.add(15, 'minutes');
            }
          } else if (
            shiftStartObj.isSameOrBefore(today) &&
            shiftEndObj.isSameOrAfter(today)
          ) {
            let todayTT = moment(
              moment().tz(storeConfig.timeZone).format('YYYY-MM-DD hh:mm A'),
              'YYYY-MM-DD hh:mm A'
            ).add(Number(orderSetup.deliveryTime), 'minutes');
            let tempTime = today.add(
              Number(orderSetup.deliveryTime),
              'minutes'
            );

            // Add current time, rounding off time and then go with default time
            const displayTime = `${orderSetup.deliveryTime} mins`;
            let timeD = {
              display: displayTime,
              alternateDisplay: 'ASAP',
              timeStamp: t.openingStart,
              displayForUse: todayTT.format('hh:mm A'),
              shiftId: t._id,
              timeObjFinal: todayTT.format('YYYY-MM-DD'),
              mTimeStamp: todayTT.valueOf(),
            };
            times.push(timeD);
            timeGroup.push(displayTime);

            const roundedUp = Math.ceil(tempTime.minute() / 15) * 15;
            tempTime = tempTime.minute(roundedUp).second(0);
            while (
              orderSetup && orderSetup.isOrderAllowedTillClosing
                ? tempTime.isSameOrBefore(shiftEndObj)
                : tempTime.isBefore(shiftEndObj)
            ) {
              let time = {
                display: tempTime.format('hh:mm A'),
                timeStamp: t.openingStart,
                displayForUse: tempTime.format('hh:mm A'),
                shiftId: t._id,
                timeObjFinal: tempTime.format('YYYY-MM-DD'),
                mTimeStamp: tempTime.valueOf(),
              };
              times.push(time);
              timeGroup.push(tempTime.format('hh:mm A'));
              tempTime.add(15, 'minutes');
            }
          }
        }
      }
      return t;
    });
    obj.times = times;
    obj.timeGroup = { displayTime: timeGroup };
    obj.valueGroup = { displayTime: times.length > 0 ? times[0].display : '' };
    return obj;
  });
  applyOvernightAsap(
    storeConfig,
    allShifts,
    orderSetup,
    deliveryTimes,
    'delivery'
  );
  if (closeDates.length > 0) {
    deliveryTimes = deliveryTimes.filter((obj) => {
      let tcDate = closeDates.filter((m) => {
        return (
          m.closeDate === obj.dateString &&
          (m.isAllClosed || m.isDeliveryClosed)
        );
      });
      if (tcDate.length > 0) return false;
      else return true;
    });
  }

  deliveryTimes = deliveryTimes.filter((p) => {
    return p.times.length !== 0;
  });
  deliveryTimes =
    deliveryTimes.length > preOrderingDays
      ? deliveryTimes.slice(0, preOrderingDays)
      : deliveryTimes;
  deliveryTimes = deliveryTimes.map((p) => {
    // ASAP stays first within its day, as in getPickupTimeArraysFun.
    p.times = sortBy(p.times, [
      (t) => (t.alternateDisplay === 'ASAP' ? 0 : 1),
      'mTimeStamp',
    ]);
    return p;
  });
  logTimeArrays('delivery', storeConfig, deliveryTimes);
  return deliveryTimes;
};

export const getCurrentShift = getCurrentShiftFun;
export const getPickupTimeArrays = getPickupTimeArraysFun;
export const getDeliveryTimeArrays = getDeliveryTimeArraysFun;
export const getRestauranTimeArrays = getRestauranTimeArraysFun;
export const getJoinedEndSeconds = getJoinedEndSecondsFun;
